import { createColumnHelper } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";
import TableHeader from "../../components/TableHeader";
import { UserRole } from "../../types";
import Switch from "../../components/Switch";
import { useState } from "react";
import { AccountHolderUser } from "../../services/accountService";
import TrashIcon from "@heroicons/react/24/solid/TrashIcon";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import Spinner from "../../components/Spinner";
import useDeleteUserMutation from "../../hooks/data/mutations/useDeleteUserMutation";
import { useSnackbar } from "../../components/Snackbar";
import HasAccess, { csrAdminRoles } from "../../components/HasAccess";
import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";

type UserStatusColumnProps = {
  id: string;
  role: UserRole;
  status: boolean;
  onStatusSwitchToggle: (id: string, value: boolean) => void;
};

function UserStatusColumn({
  id,
  role,
  status,
  onStatusSwitchToggle,
}: UserStatusColumnProps) {
  const [localStatus, setLocalstatus] = useState(status);

  return (
    <div className="flex items-center gap-3">
      {role === "ADMIN" ? (
        <Switch
          checked={localStatus}
          onChange={() => {
            setLocalstatus((p) => !p);
            onStatusSwitchToggle(id, status);
          }}
          label={""}
        />
      ) : status === null ? (
        <span className="my-0.5 h-5" />
      ) : (
        <span
          className={twJoin(
            "flex w-16 justify-center rounded-md border px-2 text-sm font-medium",
            status
              ? "border-green-200 bg-green-100 text-green-800"
              : "border-red-200 bg-red-100 text-red-700"
          )}
        >
          {status ? "Active" : "Inactive"}
        </span>
      )}
    </div>
  );
}

type UserActionsColumnProps = {
  accountHolderUser: AccountHolderUser;
  accountHolderCode: string;
};

function UserActionsColumn({
  accountHolderUser,
  accountHolderCode,
}: UserActionsColumnProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { data: currentUserAccountData } = useCurrentUserAccount();
  const isCurrentUser =
    currentUserAccountData?.username === accountHolderUser.email;
  const { mutate, isLoading } = useDeleteUserMutation(accountHolderCode);

  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  function handleDeleteUser() {
    mutate(accountHolderUser.id, {
      onSuccess() {
        openSnackbar("User successfully deleted.", {
          type: "success",
        });
      },
      onError(e) {
        openSnackbar(e.message, {
          type: "error",
        });
      },
    });
  }

  return (
    <HasAccess allowedRoles={[...csrAdminRoles]}>
      {!isCurrentUser && (
        <>
          <div className="flex justify-center">
            <TrashIcon
              className={twJoin(
                "h-5 w-5 cursor-pointer text-primary",
                accountHolderUser.status && "cursor-not-allowed text-gray-400"
              )}
              onClick={() => {
                if (!accountHolderUser.status) {
                  setShowDeleteModal(true);
                }
              }}
            />
          </div>
          <Modal
            isOpen={showDeleteModal}
            title={"Delete User"}
            titleClasses="text-left"
            panelClasses="min-w-[300px]"
            onClose={() => setShowDeleteModal(false)}
          >
            <div className="mt-5 flex flex-col">
              <p className="py-2">Are you sure you want to delete this user?</p>
              <div className="mt-4 flex justify-center gap-x-16">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowDeleteModal(false)}
                  className="w-20"
                >
                  No
                </Button>
                <Button
                  size="sm"
                  onClick={handleDeleteUser}
                  className="w-20 justify-center"
                  disabled={isLoading}
                  icon={
                    isLoading && <Spinner className="mt-0.5 mr-1 text-white" />
                  }
                >
                  Yes
                </Button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </HasAccess>
  );
}

const columnHelper = createColumnHelper<AccountHolderUser>();
export const getUserDetailsTableColumns = () => [
  columnHelper.accessor("firstName", {
    header: () => <TableHeader text="User Name" />,
    cell: (info) => {
      const firstName = info.getValue();
      const lastName = info.row.original.lastName;
      return <div className="truncate">{`${firstName} ${lastName}`}</div>;
    },
    minSize: 200,
    size: 200,
  }),
  columnHelper.accessor("email", {
    header: () => <TableHeader text="Email" />,
    cell: (info) => {
      return <div className="truncate">{info.getValue()}</div>;
    },
    minSize: 200,
    size: 300,
  }),
  columnHelper.accessor("role", {
    header: () => <TableHeader text="Role" />,
    cell: (info) => {
      return <div className="truncate font-medium">{info.getValue()}</div>;
    },
    minSize: 200,
    size: 200,
  }),
  columnHelper.accessor("status", {
    header: () => <TableHeader text="Inactive/Active" />,
    cell: (info) => {
      const id = info.row.original.id;
      const status = info.getValue();

      const { role, onStatusSwitchToggle } = info.table.options.meta as {
        role: UserRole;
        onStatusSwitchToggle: (id: string, value: boolean) => void;
      };
      return (
        <UserStatusColumn
          id={id}
          status={Boolean(status)}
          role={role}
          onStatusSwitchToggle={onStatusSwitchToggle}
        />
      );
    },
    minSize: 100,
    size: 100,
  }),
  columnHelper.accessor("lastName", {
    header: () => <TableHeader text="" />,
    cell: (info) => {
      const accountHolderUser = info.row.original;
      const { accountHolderCode } = info.table.options.meta as {
        accountHolderCode: string;
      };
      return (
        <UserActionsColumn
          accountHolderCode={accountHolderCode}
          accountHolderUser={accountHolderUser}
        />
      );
    },
    minSize: 100,
    size: 100,
  }),
];
